import { contains } from "client/Data/searching";
import { DataSourceSortableState } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useSelection,
	useSortHandler
} from "client/Hooks";
import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef
} from "react";
import styles from "./WebDavPickerOverview.module.less";
import {
	DocumentItemType,
	FileTypeFilterModel,
	FileTypeFilterKind
} from "client/Api/Documents/SharePoint/common";
import { WebDavPickerTableView, WebDavPickerTableViewHandle } from "./WebDavPickerTableView";
import { convertToGridItem, GridItem } from "../../WebDav/WebDavOverview";
import { CommonItemPickerOptions, ItemPickerMode } from "../ItemPicker";
import { WebDavPickerListView, WebDavPickerListViewHandle } from "./WebDavPickerListView";
import {
	OpenActionHandlers,
	getOpenActions,
	WebDavDocumentAction,
	WebDavDocumentActionContext
} from "../../WebDav/actions";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "../../useEditorPreference";
import {
	ensureNotNil,
	isNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { Folder } from "./ItemSelector";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";
import { WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { never } from "client_legacy/ndaw/errors";
import { getDocumentKind } from "../common";

type DataSourceFilter = {
	fileTypeFilter: FileTypeFilterModel | undefined;
};

const useDataSource = createLocalDataSource<GridItem, DataSourceFilter>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.model.name, searchTerm)
		},
		filtering: {
			applyFilter: (items: readonly GridItem[], scope: DataSourceFilter): GridItem[] => {
				return items.filter(
					item => doesMatchFileFilter(item, scope.fileTypeFilter)
				);
			}
		}
	})
	.getHook();

export type WebDavPickerOverviewProps = {
	folder: Folder;
	models: readonly FileSystemItemModel[];
	isLoadingModels: boolean;
	onOpenFolder(folder: FileSystemItemModel): void;
	onFilesSelect: (files: readonly GridItem[]) => void;
	selectedItems: readonly GridItem[];
	setIsBusy: (isBusy: boolean) => void;
	searchTerm: VersionedValue<string>;
	commonOptions: CommonItemPickerOptions;
	sorting: DataSourceSortableState<GridItem>;
	setSorting: Dispatch<SetStateAction<DataSourceSortableState<GridItem>>>;
	isMobile: boolean;
	shouldOpenFileOnClick: boolean;
	webDavConfiguration: WebDavConfiguration;
};

export const WebDavPickerOverview = (
	props: WebDavPickerOverviewProps
): ReactElement => {
	const {
		models,
		folder,
		isLoadingModels,
		onOpenFolder,
		onFilesSelect,
		selectedItems,
		searchTerm,
		commonOptions,
		sorting,
		setSorting,
		isMobile,
		shouldOpenFileOnClick,
		webDavConfiguration
	} = props;

	const alertManager: AlertManager = useAlerts();

	const scrollContainerRef: RefObject<WebDavPickerTableViewHandle | WebDavPickerListViewHandle | null>
		= useRef<WebDavPickerTableViewHandle | WebDavPickerListViewHandle>(null);

	const isMultiSelect = useMemo(
		(): boolean => commonOptions.mode === ItemPickerMode.MultipleFiles
			|| commonOptions.mode === ItemPickerMode.MultipleFilesOrOneNoteNotebooks,
		[commonOptions.mode]
	);

	const items = useMemo(
		(): readonly GridItem[] => {
			return models.map(convertToGridItem);
		},
		[models]
	);

	const { sortingFor } = useSortHandler<GridItem>(
		sorting,
		setSorting,
		true
	);

	const {
		currentItems
	} = useDataSource(
		items,
		{
			searchTerm: searchTerm.value,
			sorting,
			filtering: { fileTypeFilter: commonOptions.fileTypeFilter }
		}
	);

	const selectableItems: GridItem[] = useMemo(
		() => {
			return currentItems.filter(
				(x: GridItem): boolean => {
					if (x.model.isFolder) {
						return false;
					}

					return !checkIsDocumentDisabled(x, commonOptions.mode);
				}
			);
		},
		[commonOptions.mode, currentItems]
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		selectableItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems: onFilesSelect
		}
	);

	useEffect(
		() => {
			hasImplicitDependencyOn(
				folder,
				searchTerm,
				sorting
			);

			onFilesSelect([]);

			const scrollContainer: WebDavPickerTableViewHandle | WebDavPickerListViewHandle | null
				= scrollContainerRef.current;

			scrollContainer?.resetScrollPosition();
		},
		[
			onFilesSelect,
			folder,
			searchTerm,
			sorting
		]
	);

	const editorPreference: UserEditorPreference = useEditorPreference();

	const actionHandlers = useMemo(
		(): OpenActionHandlers => {
			return {
				openFolder: onOpenFolder,
				selectItem: (item: FileSystemItemModel) => {
					onFilesSelect(
						[
							ensureNotNil(
								currentItems.find(x => x.id === item.id)
							)
						]
					);
				}
			};
		},
		[
			currentItems,
			onFilesSelect,
			onOpenFolder
		]
	);

	const actionContext = useMemo(
		(): WebDavDocumentActionContext => {
			return {
				server: folder.server,
				folder: {
					id: folder.id,
					name: folder.name
				},
				selectedDocuments: [],
				isMobile,
				isInSelectMode: false
			};
		},
		[
			folder,
			isMobile
		]
	);

	const getAvailableOpenActions = useCallback(
		(item: FileSystemItemModel): readonly WebDavDocumentAction[] => {
			const context: WebDavDocumentActionContext = {
				...actionContext,
				selectedDocuments: [item]
			};

			const actions: readonly WebDavDocumentAction[] = getOpenActions(
				actionHandlers,
				currentItems.map(x => x.model),
				webDavConfiguration,
				alertManager,
				editorPreference
			);

			return actions.filter(x => x.canExecute(context));
		},
		[
			actionContext,
			actionHandlers,
			alertManager,
			currentItems,
			editorPreference,
			webDavConfiguration
		]
	);

	const openItemHandler = useCallback(
		(item: GridItem) => {
			if (!shouldOpenFileOnClick && !item.model.isFolder) {
				return;
			}

			getAvailableOpenActions(item.model)[0]
				?.execute({...actionContext, selectedDocuments: [item.model]});
		},
		[
			actionContext,
			getAvailableOpenActions,
			shouldOpenFileOnClick
		]
	);

	const renderContent = (): ReactElement => {
		if (isMobile) {
			return (
				<WebDavPickerListView
					isBusy={isLoadingModels}
					ref={scrollContainerRef}
					selectionFor={selectionFor}
					items={currentItems}
					onOpenItem={openItemHandler}
					mode={commonOptions.mode}
					isInSelectMode={isMultiSelect}
					getAvailableOpenActions={getAvailableOpenActions}
					actionContext={actionContext}
					hasSearch={isNotNilOrEmpty(searchTerm.value)}
				/>
			);
		}

		return (
			<WebDavPickerTableView
				isBusy={isLoadingModels}
				ref={scrollContainerRef}
				sortingFor={sortingFor}
				multiSelection={isMultiSelect ? multiSelection : undefined}
				selectionFor={selectionFor}
				items={currentItems}
				onOpenItem={openItemHandler}
				mode={commonOptions.mode}
				hasSearch={isNotNilOrEmpty(searchTerm.value)}
			/>
		);
	};

	return (
		<div className={styles.root}>
			<div className={styles.itemsContainer}>
				{renderContent()}
			</div>
		</div>
	);
};
WebDavPickerOverview.displayName = "WebDavPickerOverview";

export function checkIsDocumentDisabled(
	document: GridItem,
	mode: ItemPickerMode
): boolean {
	if (document.kind === DocumentItemType.Folder) {
		return false;
	}

	if (mode === ItemPickerMode.SingleFolder) {
		return true;
	}

	return false;
}

function doesMatchFileFilter(
	item: GridItem,
	fileTypeFilter: FileTypeFilterModel | undefined
): boolean {
	if(isNil(fileTypeFilter) || item.model.isFolder){
		return true;
	}

	if (isNil(item.extension)) {
		return false;
	}

	switch (fileTypeFilter.kind) {
		case FileTypeFilterKind.OfficeDocument:
			return getDocumentKind(item.extension) === fileTypeFilter.officeDocumentKind;
		case FileTypeFilterKind.ExtensionList:
			const { extensions } = fileTypeFilter;
			return extensions.includes(item.extension);
		default:
			never(fileTypeFilter);
	}
}